import { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { Folder, FolderOpen, ChevronRight, ArrowLeft, Star, Archive, Plus, Edit3, Trash2 } from "lucide-react";
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger,
  ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger,
} from "@/components/ui/context-menu";
import { useConfirm } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===================== Hook: folder navigation state =====================
// Tracks the currently-open folder and exposes navigation helpers.
// currentFolderId === null  → root
// currentFolderId === "__archived__" → archived view
export const useFolderNavigation = () => {
  const [currentFolderId, setCurrentFolderId] = useState(null);

  const navigate = useCallback((id) => setCurrentFolderId(id), []);
  const goRoot = useCallback(() => setCurrentFolderId(null), []);
  const goArchived = useCallback(() => setCurrentFolderId("__archived__"), []);

  return { currentFolderId, setCurrentFolderId: navigate, goRoot, goArchived };
};

// ===================== Helpers =====================
export const buildBreadcrumb = (folders, currentFolderId) => {
  const crumbs = [];
  let cur = folders.find(f => f.id === currentFolderId);
  while (cur) {
    crumbs.unshift(cur);
    cur = cur.parent_id ? folders.find(f => f.id === cur.parent_id) : null;
  }
  return crumbs;
};

export const partitionCurrentLevel = ({ folders, items, currentFolderId }) => {
  if (currentFolderId === "__archived__") {
    return {
      folders: folders.filter(f => f.archived).sort((a, b) => a.name.localeCompare(b.name)),
      items: [],
    };
  }
  return {
    folders: folders
      .filter(f => !f.archived && (f.parent_id || null) === (currentFolderId || null))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)),
    items: items
      .filter(i => (i.folder_id || null) === (currentFolderId || null))
      .sort((a, b) => (a.name || a.title || "").localeCompare(b.name || b.title || "")),
  };
};

// Recursively count how many items are inside a folder + its descendants
export const countItemsInFolder = (folderId, folders, items) => {
  const descendants = new Set([folderId]);
  let added = true;
  while (added) {
    added = false;
    folders.forEach(f => {
      if (f.parent_id && descendants.has(f.parent_id) && !descendants.has(f.id)) {
        descendants.add(f.id);
        added = true;
      }
    });
  }
  return items.filter(i => i.folder_id && descendants.has(i.folder_id)).length;
};

// ===================== FolderCard — the big folder tile in the grid =====================
export const FolderCard = ({ folder, count, onOpen, onRename, onDelete, onArchive, onColor, draggable, onDropItem }) => {
  const [isOver, setIsOver] = useState(false);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <button
          type="button"
          onClick={onOpen}
          onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
          onDragLeave={() => setIsOver(false)}
          onDrop={(e) => {
            setIsOver(false);
            const id = e.dataTransfer.getData("entityId");
            if (id && onDropItem) onDropItem(id, folder.id);
          }}
          className={`group relative flex flex-col items-center justify-end gap-2 p-4 rounded-sm border transition-all overflow-hidden h-full min-h-[180px] ${
            isOver
              ? "border-primary bg-primary/10 ring-2 ring-primary/40"
              : "border-border bg-card hover:border-primary/40 hover:bg-card/80"
          }`}
          data-testid={`folder-card-${folder.id}`}
        >
          {folder.color && (
            <span className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: folder.color }} />
          )}
          {/* Folder graphic */}
          <div className="flex-1 flex items-center justify-center w-full">
            <div className="relative">
              <Folder
                className="w-20 h-20 transition-transform group-hover:scale-105"
                style={{ color: folder.color || "#5b9cf2", fill: folder.color ? `${folder.color}33` : "#5b9cf233", strokeWidth: 1.2 }}
              />
              {typeof count === "number" && count > 0 && (
                <span className="absolute inset-0 flex items-center justify-center mt-1.5">
                  <span className="text-2xl font-serif font-bold text-foreground/90 drop-shadow-sm">{count}</span>
                </span>
              )}
            </div>
          </div>
          <div className="w-full text-center">
            <p className="text-sm font-medium text-foreground truncate" title={folder.name}>{folder.name}</p>
          </div>
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        <ContextMenuItem onClick={onOpen}>
          <FolderOpen className="w-3.5 h-3.5 mr-2" /> Open
        </ContextMenuItem>
        {onRename && (
          <ContextMenuItem onClick={onRename}>
            <Edit3 className="w-3.5 h-3.5 mr-2" /> Rename
          </ContextMenuItem>
        )}
        {onColor && (
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <span className="w-3.5 h-3.5 mr-2 rounded-sm border border-border" style={{ backgroundColor: folder.color || "transparent" }} />
              Color
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {[null, "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#a855f7", "#ec4899"].map((c, i) => (
                <ContextMenuItem key={i} onClick={() => onColor(c)}>
                  <span className="w-3.5 h-3.5 mr-2 rounded-sm border border-border" style={{ backgroundColor: c || "transparent" }} />
                  {c ? c : "None"}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}
        {onArchive && (
          <ContextMenuItem onClick={onArchive}>
            <Archive className="w-3.5 h-3.5 mr-2" /> {folder.archived ? "Unarchive" : "Archive"}
          </ContextMenuItem>
        )}
        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

// ===================== FolderBreadcrumb =====================
export const FolderBreadcrumb = ({ folders, currentFolderId, onNavigate, label = "All" }) => {
  if (currentFolderId === "__archived__") {
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="folder-breadcrumb">
        <button onClick={() => onNavigate(null)} className="hover:text-foreground transition-colors">{label}</button>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground"><Archive className="w-3 h-3 inline mr-1" />Archived</span>
      </div>
    );
  }
  const crumbs = buildBreadcrumb(folders, currentFolderId);
  return (
    <div className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap" data-testid="folder-breadcrumb">
      <button onClick={() => onNavigate(null)} className="hover:text-foreground transition-colors flex items-center gap-1" data-testid="crumb-root">
        {label}
      </button>
      {crumbs.map((c) => (
        <span key={c.id} className="flex items-center gap-1">
          <ChevronRight className="w-3 h-3 flex-shrink-0" />
          <button onClick={() => onNavigate(c.id)} className="hover:text-foreground transition-colors" data-testid={`crumb-${c.id}`}>
            {c.name}
          </button>
        </span>
      ))}
    </div>
  );
};

// ===================== FolderRail — simple left sidebar =====================
export const FolderRail = ({ folders, items, currentFolderId, onNavigate, allLabel = "All", className = "", testIdPrefix = "rail" }) => {
  const topLevel = useMemo(
    () => folders.filter(f => !f.parent_id && !f.archived).sort((a, b) => a.name.localeCompare(b.name)),
    [folders]
  );
  const archivedCount = folders.filter(f => f.archived).length;
  const totalItems = items.length;

  return (
    <aside className={`flex flex-col gap-0.5 ${className}`} data-testid={`${testIdPrefix}-folder-rail`}>
      <button
        onClick={() => onNavigate(null)}
        className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
          currentFolderId === null ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/20"
        }`}
        data-testid={`${testIdPrefix}-rail-all`}
      >
        <Star className={`w-3.5 h-3.5 ${currentFolderId === null ? "fill-primary text-primary" : ""}`} />
        <span className="flex-1 text-left">{allLabel}</span>
        <span className="text-xs text-muted-foreground/70 tabular-nums">{totalItems}</span>
      </button>
      {topLevel.map(f => {
        const count = countItemsInFolder(f.id, folders, items);
        return (
          <button
            key={f.id}
            onClick={() => onNavigate(f.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
              currentFolderId === f.id ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/20"
            }`}
            data-testid={`${testIdPrefix}-rail-folder-${f.id}`}
          >
            <Folder className="w-3.5 h-3.5 flex-shrink-0" style={{ color: f.color || undefined }} />
            <span className="flex-1 text-left truncate">{f.name}</span>
            <span className="text-xs text-muted-foreground/70 tabular-nums">{count}</span>
          </button>
        );
      })}
      {archivedCount > 0 && (
        <button
          onClick={() => onNavigate("__archived__")}
          className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors mt-2 border-t border-border/30 pt-3 ${
            currentFolderId === "__archived__" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/20"
          }`}
          data-testid={`${testIdPrefix}-rail-archived`}
        >
          <Archive className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1 text-left">Archived</span>
          <span className="text-xs text-muted-foreground/70 tabular-nums">{archivedCount}</span>
        </button>
      )}
    </aside>
  );
};

// ===================== FolderToolbar — back button + breadcrumb + new-folder action =====================
export const FolderToolbar = ({ folders, currentFolderId, onNavigate, onNewFolder, allLabel = "All", className = "" }) => {
  const parentId = useMemo(() => {
    if (currentFolderId === "__archived__") return null;
    const cur = folders.find(f => f.id === currentFolderId);
    return cur?.parent_id ?? (cur ? null : null);
  }, [folders, currentFolderId]);
  const isRoot = currentFolderId === null;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {!isRoot && (
        <button
          onClick={() => onNavigate(parentId)}
          className="p-1.5 rounded-sm border border-border bg-card hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-colors"
          title="Back"
          data-testid="folder-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
      )}
      <FolderBreadcrumb folders={folders} currentFolderId={currentFolderId} onNavigate={onNavigate} label={allLabel} />
      <div className="flex-1" />
      {onNewFolder && (
        <button
          onClick={onNewFolder}
          className="px-2 py-1 rounded-sm border border-border bg-card hover:bg-muted/30 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
          title="New folder here"
          data-testid="folder-new-btn"
        >
          <Plus className="w-3 h-3" /> New Folder
        </button>
      )}
    </div>
  );
};

// ===================== Folder CRUD helpers (axios) =====================
export const folderApi = {
  list: (campaignId, entityType) =>
    axios.get(`${API}/campaigns/${campaignId}/folders/${entityType}`, { withCredentials: true }).then(r => r.data),
  create: (campaignId, entityType, body) =>
    axios.post(`${API}/campaigns/${campaignId}/folders/${entityType}`, body, { withCredentials: true }).then(r => r.data),
  update: (folderId, body) =>
    axios.put(`${API}/folders/${folderId}`, body, { withCredentials: true }).then(r => r.data),
  remove: (folderId) =>
    axios.delete(`${API}/folders/${folderId}`, { withCredentials: true }).then(r => r.data),
  moveItem: (campaignId, entityType, itemId, folderId) =>
    axios.put(`${API}/campaigns/${campaignId}/${entityType}/${itemId}/folder`, { folder_id: folderId || null }, { withCredentials: true }).then(r => r.data),
};

// ===================== useFolderCrud — convenience hook for pages =====================
// Loads folders, exposes mutation helpers, returns navigation state.
export const useFolderCrud = ({ entityType, onRefresh }) => {
  const { campaignId } = useParams();
  const [folders, setFolders] = useState([]);
  const nav = useFolderNavigation();
  const confirm = useConfirm();

  const refresh = useCallback(async () => {
    try {
      const data = await folderApi.list(campaignId, entityType);
      setFolders(data);
    } catch { /* ignore */ }
  }, [campaignId, entityType]);

  // Auto-load folders on mount (and whenever the campaign or entity type changes).
  // Without this, the folder list stayed empty on page load and only appeared
  // after the user triggered a mutation (create/rename/move).
  useEffect(() => { refresh(); }, [refresh]);

  const createHere = useCallback(async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt("New folder name");
    if (!name?.trim()) return;
    try {
      await folderApi.create(campaignId, entityType, { name: name.trim(), parent_id: nav.currentFolderId === "__archived__" ? null : nav.currentFolderId || null });
      toast.success("Folder created");
      await refresh();
    } catch { toast.error("Failed to create folder"); }
  }, [campaignId, entityType, nav.currentFolderId, refresh]);

  const renameFolder = useCallback(async (folder) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt("Rename folder", folder.name);
    if (!next?.trim() || next === folder.name) return;
    try {
      await folderApi.update(folder.id, { name: next.trim() });
      toast.success("Renamed");
      await refresh();
    } catch { toast.error("Failed to rename"); }
  }, [refresh]);

  const setFolderColor = useCallback(async (folder, color) => {
    try {
      await folderApi.update(folder.id, { color });
      await refresh();
    } catch { toast.error("Failed to set colour"); }
  }, [refresh]);

  const toggleArchive = useCallback(async (folder) => {
    try {
      await folderApi.update(folder.id, { archived: !folder.archived });
      toast.success(folder.archived ? "Unarchived" : "Archived");
      await refresh();
    } catch { toast.error("Failed to archive"); }
  }, [refresh]);

  const deleteFolder = useCallback(async (folder) => {
    const ok = await confirm("Delete folder?", `"${folder.name}" will be removed. Its items become unfiled (not deleted). Continue?`);
    if (!ok) return;
    try {
      await folderApi.remove(folder.id);
      toast.success("Folder deleted");
      if (nav.currentFolderId === folder.id) nav.goRoot();
      await refresh();
      onRefresh?.();
    } catch { toast.error("Failed to delete folder"); }
  }, [confirm, nav, refresh, onRefresh]);

  const moveItem = useCallback(async (itemId, folderId) => {
    try {
      await folderApi.moveItem(campaignId, entityType, itemId, folderId);
      toast.success("Moved");
      onRefresh?.();
    } catch { toast.error("Failed to move"); }
  }, [campaignId, entityType, onRefresh]);

  return {
    folders,
    setFolders,
    refresh,
    createHere,
    renameFolder,
    setFolderColor,
    toggleArchive,
    deleteFolder,
    moveItem,
    ...nav,
  };
};
